import { useCallback, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { toast } from 'sonner';
import {
  AppNode,
  NodeType,
  NodeStatus,
  DetailPageStage,
  DetailPageData,
  DetailPagePlannerData,
  DetailPageDesignerData,
  SkillRunState,
  SplitPrompt,
  CompetitorScreen,
} from '@/types';
import { useNodeStore } from '@/stores/nodeStore';
import { useAgentStore } from '@/stores/agentStore';
import { useProjectStore } from '@/stores/projectStore';
import { projectApi } from '@/services/projectApi';
import { normalizeAssetUrl } from '@/utils/assetUtils';
import { scanCompetitorImage, cropCompetitorScreens, findFirstLongImageUrl } from '@/utils/competitorScreenSplit';
import { defaultPlannerData, defaultDesignerData, deriveStage, countArchitectureBlocks } from './useDetailPageState';
import { useNodeSkillSession, NodeSkillAutoReplyRule } from './useNodeSkillSession';

export interface UseDetailPageActionsOptions {
  data: AppNode;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  /** 统一生成编排入口（EditorLayout handleNodeAction 下行）：分块出图创建节点后自动触发生图 */
  onNodeAction?: (id: string, prompt?: string) => void;
}

/**
 * 从 store 实时读取当前节点的 planner 数据
 * 避免在 useCallback 依赖 planner，从而保证回调函数身份稳定
 */
function readCurrentPlanner(nodeId: string): DetailPagePlannerData {
  const node = useNodeStore.getState().nodeMap.get(nodeId);
  const nodeData = node?.data as DetailPageData | undefined;
  return nodeData?.planner ?? defaultPlannerData();
}

function readCurrentDesigner(nodeId: string): DetailPageDesignerData {
  const node = useNodeStore.getState().nodeMap.get(nodeId);
  const nodeData = node?.data as DetailPageData | undefined;
  return nodeData?.designer ?? defaultDesignerData();
}

function collectAssetAttachments(nodeIds: string[]): Array<Record<string, any>> {
  const store = useNodeStore.getState();
  const result: Array<Record<string, any>> = [];
  for (const id of nodeIds) {
    const node = store.nodeMap.get(id);
    const url = (node?.data as { image?: string } | undefined)?.image?.trim();
    if (url) {
      result.push({ dataUrl: url, thumbnailUrl: url, type: 'image/png', name: node?.title || '图片' });
    }
  }
  return result;
}

/** 存量 skill-result 结果节点一次性清理（§9 迁移）：按 sourceNodeId 全量匹配（不区分 resultKind），deleteNodes 自动清连线 */
export function cleanupSkillResultNodes(sourceNodeId: string) {
  const { nodes, deleteNodes } = useNodeStore.getState();
  const ids = nodes
    .filter((n) => {
      if (n.type !== NodeType.PROMPT_INPUT) return false;
      const d = n.data as { subMode?: string; skillResult?: { sourceNodeId?: string } } | undefined;
      return d?.subMode === 'skill-result' && d.skillResult?.sourceNodeId === sourceNodeId;
    })
    .map((n) => n.id);
  if (ids.length > 0) deleteNodes(ids);
}

function parseAnchoredSection(text: string, startAnchor: string, endAnchor?: string): string {
  const startIdx = text.indexOf(startAnchor);
  if (startIdx < 0) return '';
  const from = startIdx + startAnchor.length;
  const endIdx = endAnchor ? text.indexOf(endAnchor, from) : -1;
  return text.slice(from, endIdx >= 0 ? endIdx : undefined).trim();
}

/** 多候选终止锚点版：取最早出现的终止锚点（竞品分析可被跳过，其后直接是风格确定段） */
function parseAnchoredSectionToAny(text: string, startAnchor: string, endAnchors: string[]): string {
  const startIdx = text.indexOf(startAnchor);
  if (startIdx < 0) return '';
  const from = startIdx + startAnchor.length;
  let endIdx = text.length;
  for (const anchor of endAnchors) {
    const idx = text.indexOf(anchor, from);
    if (idx >= 0 && idx < endIdx) endIdx = idx;
  }
  return text.slice(from, endIdx).trim();
}

/** P2.5 切屏边界确认表行（契约：屏序 | 选定边界行位置% | 模块类型 | 该屏版式要点） */
export interface ParsedScreenBoundary {
  /** 切割点位置%（(0,100) 开区间；100% 或缺失 = 末屏元数据行，无切割点） */
  boundaryPct: number | null;
  moduleType: string;
  styleNotes: string;
}

/** 幻觉偏移校验（§3.3-3 闸门）：偏差 ≤0.5% 视为 LLM 抄表抖动，吸附到像素级候选；＞0.5% 视为凭空估算，
 *  丢弃切割点（模块元数据保留）。契约要求位置%必须直接取自候选行清单，禁止透传候选外原值——
 *  透传会产生候选外切割点（如 LLM 写 99%、实际末边界 97.8%，裁出 1% 碎片屏）；无候选清单时透传（向后兼容） */
function snapBoundaryPct(pct: number | null, candidates?: number[]): number | null {
  if (pct === null || !candidates || candidates.length === 0) return pct;
  let nearest = candidates[0];
  let minDiff = Math.abs(pct - nearest);
  for (const cand of candidates) {
    const diff = Math.abs(pct - cand);
    if (diff < minDiff) { minDiff = diff; nearest = cand; }
  }
  return minDiff <= 0.5 ? nearest : null;
}

/** 解析【切屏边界确认表】锚点段内的 markdown 表格（LLM 仅做离散选择，位置%直接取自候选行清单；
 * 传入扫描候选清单时对每个切割点做吸附/丢弃校验） */
export function parseScreenBoundaryTable(fullText: string, candidates?: number[]): ParsedScreenBoundary[] {
  const startIdx = fullText.indexOf('【切屏边界确认表】');
  if (startIdx < 0) return [];
  const rows: ParsedScreenBoundary[] = [];
  for (const rawLine of fullText.slice(startIdx).split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('|')) {
      if (rows.length > 0) break;
      continue;
    }
    const cells = line.split('|').slice(1, -1).map((c) => c.trim());
    if (cells.length < 3) continue;
    if (cells.some((c) => /^:?-{2,}:?$/.test(c))) continue;
    const joined = cells.join('');
    if (joined.includes('屏序') || joined.includes('位置%')) continue;
    // 兼容 4 列（屏序|位置|模块|要点）与 3 列（位置|模块|要点）
    const pctIdx = cells.length >= 4 ? 1 : 0;
    const pct = parseFloat(cells[pctIdx].match(/\d+(?:\.\d+)?/)?.[0] ?? '');
    if (!Number.isFinite(pct)) continue;
    rows.push({
      boundaryPct: snapBoundaryPct(pct > 0 && pct < 100 ? pct : null, candidates),
      moduleType: cells[pctIdx + 1] || '',
      styleNotes: cells[pctIdx + 2] || '',
    });
  }
  return rows;
}

/** 切片最小高度%（低于此值的切片视为碎片屏，裁切前合并进邻屏；正常 8 屏均分约 12.5%/屏） */
export const MIN_SLICE_HEIGHT_PCT = 5;

/** 确认表切割点 → 实际裁切序列：去重升序后做最小屏高过滤（防碎片屏）：
 *  与前一有效切点间距不足（如候选含 2% 碎片值、相邻候选 10.2/11.9 全选）时丢弃后者，
 *  末屏过矮（如末切点 99% 留下 1% 碎片尾屏）时回退丢弃尾切点 */
export function planScreenCuts(boundaries: ParsedScreenBoundary[]): number[] {
  const cuts = [...new Set(boundaries.map((b) => b.boundaryPct).filter((p): p is number => p != null))]
    .sort((a, b) => a - b);
  const filtered: number[] = [];
  for (const cut of cuts) {
    const prev = filtered.length > 0 ? filtered[filtered.length - 1] : 0;
    if (cut - prev < MIN_SLICE_HEIGHT_PCT) continue;
    filtered.push(cut);
  }
  while (filtered.length > 0 && 100 - filtered[filtered.length - 1] < MIN_SLICE_HEIGHT_PCT) {
    filtered.pop();
  }
  return filtered;
}

/** 切片元数据按切割点 pct 值配对（禁止按确认表行序配对：行被丢弃/去重或表序乱序时行序≠切片序，
 *  会引发切片图与描述文字整体错位一格）；末屏（下边界 100%）取表尾 100% 元数据行（boundaryPct=null） */
export function resolveSliceMetaByEdge(boundaries: ParsedScreenBoundary[], bottomEdgePct: number): ParsedScreenBoundary | undefined {
  if (bottomEdgePct < 100) {
    return boundaries.find((b) => b.boundaryPct === bottomEdgePct);
  }
  const tail = [...boundaries].reverse().find((b) => b.boundaryPct === null);
  return tail ?? boundaries[boundaries.length - 1];
}

/** 分屏提示词标题行契约（§5.4）：
 *  现行契约（page_prompt 模板实际输出）：**第N区块[-子标识]（S/D/T…）：**
 *  如 **第1区块-A（S，1屏）：** / **第2区块（D，2屏合并，比例9:21）：** / **第N区块（T，3屏合并，比例1:3）：** */
const SPLIT_PROMPT_HEADER_RE =
  /^\*{0,2}\s*第\s*(\d+)\s*区块\s*(?:[-–—:：]\s*([^*（(]+?)\s*)?[（(]\s*([SDTsdt])[^*）)]*?[）)]\s*[-–—:：]?\s*\*{0,2}\s*$/;
/** 旧契约兜底：**第N区块-单元类型-主标题** */
const SPLIT_PROMPT_HEADER_LEGACY_RE =
  /^\*{1,2}\s*第\s*(\d+)\s*区块\s*[-–—:：]\s*([^-*]+?)\s*[-–—:：]\s*(.+?)\s*\*{1,2}\s*$/;

/** 单元类型归一化：S/D/T 字面量或中文描述（首屏/详情/尾屏）均归一到 'S'|'D'|'T' */
function normalizeUnitType(raw: string): SplitPrompt['unitType'] {
  const s = raw.trim().toUpperCase();
  if (s === 'S' || s.includes('首屏')) return 'S';
  if (s === 'T' || s.includes('尾屏')) return 'T';
  return 'D';
}

/** 区块显示名：第一区块…第十区块（超出一转阿拉伯数字兜底） */
export function formatBlockLabel(index: number): string {
  return `第${['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'][index - 1] ?? index}区块`;
}

/** 分块版式 → 出图节点画幅比例：S 首屏 9:16 / D 详情 9:21 / T 尾屏 1:3（未知类型兜底 3:4，与旧硬编码一致） */
const UNIT_TYPE_ASPECT_RATIO: Record<SplitPrompt['unitType'], string> = {
  S: '9:16',
  D: '9:21',
  T: '1:3',
};

/** 分块出图节点竖排堆叠参数：卡片短边固定 420（与 GenerateNode SHORT_EDGE 同源）；
 * 标题 InverseScaleBox 悬浮于卡片上方、不占卡片高度但占画布空间，计入占位高度防上下重叠；
 * 各竖排内部行距统一小间隙，整组垂直居中基准与竞品图列（PLANNER_HEIGHT 回退常量）同值 */
const BLOCK_NODE_SHORT_EDGE = 420;
const BLOCK_NODE_TITLE_ALLOWANCE = 44;
/** 每竖排最多堆叠的分块数（满 4 个横向开新竖排） */
const BLOCK_STACK_PER_COLUMN = 4;
/** 各竖排内部相邻节点的统一小行距 / 竖排之间的列距（画布像素） */
const BLOCK_STACK_GAP_Y = 60;
const BLOCK_STACK_GAP_X = 200;
const DETAIL_PAGE_HEIGHT_FALLBACK = 1040;

/** 分块出图节点画布占位高度：卡片高度（短边 420 × 画幅比）+ 标题悬浮占位 */
function blockNodeFootprintHeight(unitType: SplitPrompt['unitType']): number {
  const [w, h] = (UNIT_TYPE_ASPECT_RATIO[unitType] ?? '3:4').split(':').map(Number);
  return Math.round((BLOCK_NODE_SHORT_EDGE * h) / w) + BLOCK_NODE_TITLE_ALLOWANCE;
}

/** 主文案行（后端契约每块首模块：- **主文案**："产品核心吸引点"），块标题缺失时提取为块标题；行尾容忍标点 */
const MAIN_COPY_LINE_RE =
  /^\s*(?:[-*]\s*)?\*{0,2}\s*主文案\s*\*{0,2}\s*[:：]\s*[“"']?(.+?)[”"']?\s*\*{0,2}\s*[。.!！？?，,]?\s*$/m;

/** D/T 单元无独立主文案行，主文案嵌套于文案排版子项（模板原文：- 上方文案：主文案"xxx"，副文案…），值止于引号闭口/分隔标点/行尾 */
const NESTED_MAIN_COPY_RE =
  /(?:上方|中间|下方)文案\s*\*{0,2}\s*[:：]\s*\*{0,2}\s*主文案\s*\*{0,2}\s*[：:]?\s*(?:[“"']([^”"']+)[”"']|(.+?))(?:\s*[,，。.!！？?（(]|\s*$)/m;

/** 块主文案提取：独立主文案行（S 单元）优先，文案排版子项嵌套形态（D/T 单元）兜底 */
export function extractMainCopyFromContent(content: string): string {
  const direct = content.match(MAIN_COPY_LINE_RE)?.[1];
  if (direct?.trim()) return direct.trim();
  const nested = content.match(NESTED_MAIN_COPY_RE);
  return (nested?.[1] ?? nested?.[2] ?? '').trim();
}

/** 块标题提取：旧契约主标题 > 新契约「-」后描述段（排除 A/B/C 类子标识）> 空串（由块内容主文案提取兜底） */
function resolveBlockTitle(sub: string | undefined, legacyTitle: string | undefined): string {
  if (legacyTitle?.trim()) return legacyTitle.trim();
  const s = sub?.trim();
  if (s && !/^[A-Za-z]$/.test(s)) return s;
  return '';
}

/** 旧解析器兜底标题形态（主文案提取契约引入前持久化的「第N区块」，用于存量数据识别重解析） */
const LEGACY_FALLBACK_TITLE_RE = /^第\d+区块$/;

/** 显示层块标题兜底：空标题或存量「第N区块」占位标题时，实时从块内容提取主文案 */
export function resolveBlockDisplayTitle(block: Pick<SplitPrompt, 'title' | 'content'>): string {
  const t = block.title?.trim() ?? '';
  if (t && !LEGACY_FALLBACK_TITLE_RE.test(t)) return t;
  return extractMainCopyFromContent(block.content) || t;
}

/** 按区块标题行切分全文为 splitPrompts[]（标题行独占一行，其后内容归入该块） */
export function parseSplitPrompts(text: string): SplitPrompt[] {
  const prompts: SplitPrompt[] = [];
  let current: {
    index: number;
    unitType: SplitPrompt['unitType'];
    title: string;
    contentLines: string[];
  } | null = null;
  const flush = () => {
    if (!current) return;
    // 区块唯一性后端仅靠提示词约束（无程序化去重），此处按 index 兜底去重（保留首块），防渲染 key 冲突与出图状态误判
    const idx = current.index;
    if (prompts.some((p) => p.index === idx)) return;
    const content = current.contentLines.join('\n').trim();
    prompts.push({
      index: current.index,
      unitType: current.unitType,
      title: current.title || extractMainCopyFromContent(content),
      content,
    });
  };
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    const main = line.match(SPLIT_PROMPT_HEADER_RE);
    const legacy = main ? null : line.match(SPLIT_PROMPT_HEADER_LEGACY_RE);
    if (main || legacy) {
      flush();
      const index = parseInt((main ?? legacy)![1], 10) || prompts.length + 1;
      const unitRaw = main ? main[3] : (legacy as RegExpMatchArray)[2];
      current = {
        index,
        unitType: normalizeUnitType(unitRaw),
        title: resolveBlockTitle(main?.[2], legacy?.[3]),
        contentLines: [],
      };
    } else if (current) {
      current.contentLines.push(rawLine);
    }
  }
  flush();
  return prompts;
}

export function useDetailPageActions(options: UseDetailPageActionsOptions) {
  const { data, onNodeUpdate, onAddNode, onNodeAction } = options;
  // 分块出图排布取详情页节点实测高度用（节点组件渲染在 ReactFlow 内，上下文可用）
  const reactFlowInstance = useReactFlow();

  const validatePlanner = useCallback(() => {
    const current = readCurrentPlanner(data.id);
    // 面板管理数组 + 手动连线接入，任一来源有产品图即可启动
    const productTotal = (current.productImageNodeIds?.length ?? 0) + (current.connectedProductImageNodeIds?.length ?? 0);
    if (productTotal <= 0) {
      return '请先添加产品图';
    }
    // 竞品图数量为 0 即代表跳过竞品分析，无需强制校验
    return null;
  }, [data.id]);

  const persistPlannerRunState = useCallback((runState: SkillRunState) => {
    const nodeStatus = runState.status === 'completed'
      ? NodeStatus.SUCCESS
      : runState.status === 'error'
        ? NodeStatus.ERROR
        : runState.status === 'idle'
          ? NodeStatus.IDLE
          : NodeStatus.WORKING;
    useNodeStore.getState().updateNode(
      data.id,
      { planner: { ...readCurrentPlanner(data.id), runState } },
      undefined,
      undefined,
      nodeStatus,
    );
  }, [data.id]);

  const plannerSession = useNodeSkillSession({ onRunStatePersist: persistPlannerRunState });

  /** P2.5 确认表裁切落库防重入（同一会话全文解析会多次触发，仅首触生效） */
  const screenCropStartedRef = useRef(false);

  /** dataURL 转 File（切块资产上传用） */
  function dataUrlToFile(dataUrl: string, name: string): File {
    const [meta, b64] = dataUrl.split(',');
    const mime = meta.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], name, { type: mime });
  }

  /** P2.5 切屏裁切落库：按确认表边界裁切第一张长图竞品图（与启动扫描同判定同顺序），切块上传为资产后写入 planner.competitorScreens。
   *  失败/长图缺失时回写 undefined 回落整图模式（P2 基础行为）。 */
  const cropAndPersistScreens = useCallback(async (boundaries: ParsedScreenBoundary[]) => {
    const nodeId = data.id;
    const current = readCurrentPlanner(nodeId);
    // 空数组 = 切屏进行中（面板据此显示生成中态）；失败路径回写 undefined
    useNodeStore.getState().updateNode(nodeId, {
      planner: { ...readCurrentPlanner(nodeId), competitorScreens: [] },
    });
    try {
      const mergedCompetitorIds = [
        ...(current.competitorImageNodeIds ?? []),
        ...(current.connectedCompetitorImageNodeIds ?? []),
      ];
      const urls = collectAssetAttachments(mergedCompetitorIds).map((a) => a.dataUrl);
      const longUrl = await findFirstLongImageUrl(urls);
      if (!longUrl) throw new Error('长图竞品图不存在或已删除');
      const cuts = planScreenCuts(boundaries);
      if (cuts.length === 0) throw new Error('确认表无有效切割点');
      const dataUrls = await cropCompetitorScreens(longUrl, cuts);
      // 段元数据按切割点 pct 值配对（行被丢弃/去重时行序≠切片序，按行序配对会整体错位一格）
      const edges = [0, ...cuts, 100];
      const { currentProjectId } = useProjectStore.getState();
      const screens: CompetitorScreen[] = [];
      for (let i = 0; i < dataUrls.length; i++) {
        const meta = resolveSliceMetaByEdge(boundaries, edges[i + 1]);
        let url = dataUrls[i];
        if (currentProjectId) {
          const asset = await projectApi.uploadAsset(
            currentProjectId,
            dataUrlToFile(url, `competitor_screen_${nodeId}_${i + 1}.jpg`),
            { source: 'competitor_screen_split', node_id: nodeId },
          );
          url = normalizeAssetUrl(asset.url);
        }
        screens.push({
          index: i + 1,
          url,
          topPct: edges[i],
          heightPct: edges[i + 1] - edges[i],
          moduleType: meta?.moduleType || '连续版面',
          styleNotes: meta?.styleNotes || undefined,
        });
      }
      useNodeStore.getState().updateNode(nodeId, {
        planner: { ...readCurrentPlanner(nodeId), competitorScreens: screens },
      });
    } catch (err) {
      console.error('[P2.5] 竞品屏切块裁切/上传失败，回落整图模式:', err);
      useNodeStore.getState().updateNode(nodeId, {
        planner: { ...readCurrentPlanner(nodeId), competitorScreens: undefined },
      });
    }
  }, [data.id]);

  const applyPlannerResult = useCallback((fullText: string) => {
    const productAnalysis = parseAnchoredSectionToAny(fullText, '### 产品分析', ['### 竞品分析', '### 风格确定']);
    const competitorAnalysis = parseAnchoredSectionToAny(fullText, '### 竞品分析', ['### 风格确定']);
    // P1 竞品风格传导：档案切片止于规避项锚点，规避项止于切屏确认表/风格确定
    const competitorStyleProfile = parseAnchoredSectionToAny(fullText, '【竞品风格档案】', ['【须规避项】', '### 风格确定']);
    const competitorStyleBlacklist = parseAnchoredSectionToAny(fullText, '【须规避项】', ['【切屏边界确认表】', '### 风格确定']);
    const screenBoundaries = parseScreenBoundaryTable(fullText, readCurrentPlanner(data.id).screenBoundaryCandidates);
    const copywritingResult = parseAnchoredSection(fullText, '### 文案策划', '### 详情页架构设计');
    const architectureTable = parseAnchoredSection(fullText, '### 详情页架构设计', '### 风格确认');
    if (!architectureTable) return;
    const latest = readCurrentPlanner(data.id);
    if (latest.isPlannerComplete && latest.architectureTable === architectureTable) return;
    // 智能推荐（style 为空）时，从「设计风格已确定」确认语中回写实际风格，供 designer 继承与摘要展示
    const confirmedStyle = fullText.match(/设计风格已确定[：:]\s*[「\[]?([^\s」\]】。，,；;]{1,15})/)?.[1];
    // 四切片一次原子写入 planner data（查看面板数据源，刷新/恢复后仍完整）；不再创建右侧结果节点
    useNodeStore.getState().updateNode(data.id, {
      planner: {
        ...latest,
        style: latest.style || confirmedStyle || '',
        productAnalysis: productAnalysis || undefined,
        competitorAnalysis,
        competitorStyleProfile: competitorStyleProfile || undefined,
        competitorStyleBlacklist: competitorStyleBlacklist || undefined,
        copywritingResult: copywritingResult || undefined,
        architectureTable,
        hasArchitectureTable: true,
        isPlannerComplete: true,
      },
    });
    // P2.5 长图切屏：确认表解析成功时触发裁切落库（异步不阻塞解析；重复触发由 ref 拦截）
    if (screenBoundaries.length > 0 && !screenCropStartedRef.current) {
      screenCropStartedRef.current = true;
      void cropAndPersistScreens(screenBoundaries);
    }
  }, [data.id, cropAndPersistScreens]);

  useEffect(() => {
    const runState = plannerSession.runState;
    const isStyleConfirmation = runState.status === 'waiting_ask' && runState.currentStep === 'style_confirmation';
    if (!isStyleConfirmation && runState.status !== 'completed') return;
    applyPlannerResult(plannerSession.getStreamText());
  }, [plannerSession.runState, plannerSession.getStreamText, applyPlannerResult]);

  const persistDesignerRunState = useCallback((runState: SkillRunState) => {
    const nodeStatus = runState.status === 'completed'
      ? NodeStatus.SUCCESS
      : runState.status === 'error'
        ? NodeStatus.ERROR
        : runState.status === 'idle'
          ? NodeStatus.IDLE
          : NodeStatus.WORKING;
    useNodeStore.getState().updateNode(
      data.id,
      { designer: { ...readCurrentDesigner(data.id), runState } },
      undefined,
      undefined,
      nodeStatus,
    );
  }, [data.id]);

  const designerSession = useNodeSkillSession({ onRunStatePersist: persistDesignerRunState });

  /** prompt_writing 终态/流完结解析：保留全文 promptResult + 切分 splitPrompts（§5.4） */
  const applyDesignerResult = useCallback((fullText: string) => {
    const splitPrompts = parseSplitPrompts(fullText);
    const latest = readCurrentDesigner(data.id);
    if (latest.promptResult === fullText) return;
    // 解析失败不静默（E8）：照常写 promptResult 并切 CONSUME（分屏区由空态承接），仅跳过 splitPrompts 写入（重跑入口已清空，此处不覆盖）
    useNodeStore.getState().updateNode(data.id, {
      stage: DetailPageStage.CONSUME,
      designer: { ...latest, promptResult: fullText, ...(splitPrompts.length > 0 ? { splitPrompts } : {}) },
    });
  }, [data.id]);

  useEffect(() => {
    const runState = designerSession.runState;
    const isPromptWriting = runState.status === 'waiting_ask' && runState.currentStep === 'prompt_writing';
    if (!isPromptWriting && runState.status !== 'completed') return;
    applyDesignerResult(designerSession.getStreamText());
  }, [designerSession.runState, designerSession.getStreamText, applyDesignerResult]);

  /**
   * 根据产品图数量变化，自动创建/移除媒体上传节点并维护连线
   * 生成的媒体节点排列在详情页策划师节点左侧，命名为"产品图N"
   */
  const syncProductImageNodes = useCallback((newCount: number) => {
    if (!onAddNode) return;

    const current = readCurrentPlanner(data.id);
    const existingIds = current.productImageNodeIds ?? [];
    const currentCount = existingIds.length;

    if (newCount === currentCount) return;

    // 媒体上传节点尺寸（空状态默认 16:9）
    const MEDIA_NODE_WIDTH = 747;
    const MEDIA_NODE_HEIGHT = 420;
    const GAP_X = 200;  // 详情页节点与媒体节点之间的水平间距
    const GAP_Y = 60;   // 媒体节点之间的垂直间距
    const PLANNER_HEIGHT = 1040;

    // 媒体节点水平位置：在详情页节点左侧，留出连线空间
    const mediaX = data.x - MEDIA_NODE_WIDTH - GAP_X;
    // 垂直居中于详情页策划师节点
    const totalHeight = newCount > 0
      ? newCount * MEDIA_NODE_HEIGHT + (newCount - 1) * GAP_Y
      : 0;
    const startY = data.y + PLANNER_HEIGHT / 2 - totalHeight / 2;

    let updatedIds: string[];

    if (newCount > currentCount) {
      // 新增节点
      const toCreate = newCount - currentCount;
      const newIds: string[] = [];
      for (let i = 0; i < toCreate; i++) {
        const nodeIndex = currentCount + i;
        const y = startY + nodeIndex * (MEDIA_NODE_HEIGHT + GAP_Y);
        const nodeId = onAddNode(NodeType.MEDIA_UPLOAD, { x: mediaX, y });
        if (nodeId) {
          newIds.push(nodeId);
          useNodeStore.getState().addConnection(nodeId, data.id, `${data.id}-target-left-product`);
        }
      }
      updatedIds = [...existingIds, ...newIds];
    } else {
      // 移除多余节点
      const toRemove = existingIds.slice(newCount);
      if (toRemove.length > 0) {
        useNodeStore.getState().deleteNodes(toRemove);
      }
      updatedIds = existingIds.slice(0, newCount);
    }

    // 重新排列所有产品图节点，保持整齐
    const store = useNodeStore.getState();
    store.updateNodePositions(
      updatedIds.map((id, idx) => ({
        id,
        x: mediaX,
        y: startY + idx * (MEDIA_NODE_HEIGHT + GAP_Y),
      }))
    );
    // 统一命名为"产品图N"（N 从 1 开始）
    updatedIds.forEach((id, idx) => {
      store.updateNode(id, {}, undefined, `产品图${idx + 1}`);
    });

    onNodeUpdate(data.id, {
      planner: { ...current, productImageCount: newCount, productImageNodeIds: updatedIds },
    });
  }, [data.id, data.x, data.y, onAddNode, onNodeUpdate]);

  /**
   * 根据竞品图数量变化，自动创建/移除媒体上传节点并维护连线
   * 生成的媒体节点排列在产品图节点的左侧（第二列），命名为"竞品图N"
   */
  const syncCompetitorImageNodes = useCallback((newCount: number) => {
    if (!onAddNode) return;

    const current = readCurrentPlanner(data.id);
    const existingIds = current.competitorImageNodeIds ?? [];
    const currentCount = existingIds.length;

    if (newCount === currentCount) return;

    // 媒体上传节点尺寸（空状态默认 16:9）
    const MEDIA_NODE_WIDTH = 747;
    const MEDIA_NODE_HEIGHT = 420;
    const GAP_X = 200;  // 列与列之间的水平间距
    const GAP_Y = 60;   // 媒体节点之间的垂直间距
    const PLANNER_HEIGHT = 1040;

    // 竞品图节点水平位置：在产品图节点的左侧第二列
    const competitorX = data.x - 2 * (MEDIA_NODE_WIDTH + GAP_X);
    // 垂直居中于详情页策划师节点
    const totalHeight = newCount > 0
      ? newCount * MEDIA_NODE_HEIGHT + (newCount - 1) * GAP_Y
      : 0;
    const startY = data.y + PLANNER_HEIGHT / 2 - totalHeight / 2;

    let updatedIds: string[];

    if (newCount > currentCount) {
      // 新增节点
      const toCreate = newCount - currentCount;
      const newIds: string[] = [];
      for (let i = 0; i < toCreate; i++) {
        const nodeIndex = currentCount + i;
        const y = startY + nodeIndex * (MEDIA_NODE_HEIGHT + GAP_Y);
        const nodeId = onAddNode(NodeType.MEDIA_UPLOAD, { x: competitorX, y });
        if (nodeId) {
          newIds.push(nodeId);
          useNodeStore.getState().addConnection(nodeId, data.id, `${data.id}-target-left-competitor`);
        }
      }
      updatedIds = [...existingIds, ...newIds];
    } else {
      // 移除多余节点
      const toRemove = existingIds.slice(newCount);
      if (toRemove.length > 0) {
        useNodeStore.getState().deleteNodes(toRemove);
      }
      updatedIds = existingIds.slice(0, newCount);
    }

    // 重新排列所有竞品图节点，保持整齐
    const store = useNodeStore.getState();
    store.updateNodePositions(
      updatedIds.map((id, idx) => ({
        id,
        x: competitorX,
        y: startY + idx * (MEDIA_NODE_HEIGHT + GAP_Y),
      }))
    );
    // 统一命名为"竞品图N"（N 从 1 开始）
    updatedIds.forEach((id, idx) => {
      store.updateNode(id, {}, undefined, `竞品图${idx + 1}`);
    });

    onNodeUpdate(data.id, {
      planner: {
        ...current,
        skipCompetitor: false,
        competitorImageCount: newCount,
        competitorImageNodeIds: updatedIds,
      },
    });
  }, [data.id, data.x, data.y, onAddNode, onNodeUpdate]);

  /**
   * 跳过竞品分析：删除所有竞品图节点
   */
  const skipCompetitorAnalysis = useCallback(() => {
    const current = readCurrentPlanner(data.id);
    const existingIds = current.competitorImageNodeIds ?? [];

    if (existingIds.length > 0) {
      useNodeStore.getState().deleteNodes(existingIds);
    }

    onNodeUpdate(data.id, {
      planner: {
        ...current,
        skipCompetitor: !current.skipCompetitor,
        competitorImageCount: 0,
        competitorImageNodeIds: [],
      },
    });
  }, [data.id, onNodeUpdate]);

  /**
   * 产品卖点模式切换：
   * - auto（智能创建）：删除手动创建的自己编写内容节点，清空 sellingPoints
   * - manual（手动创建）：在详情页策划师节点底部创建一个自己编写内容节点，连接到左边连接点
   */
  const syncSellingPointNode = useCallback((mode: 'auto' | 'manual') => {
    const current = readCurrentPlanner(data.id);
    const existingIds = current.sellingPointNodeIds ?? [];

    if (mode === 'auto') {
      // 智能创建：删除已有的自己编写内容节点
      if (existingIds.length > 0) {
        useNodeStore.getState().deleteNodes(existingIds);
      }
      onNodeUpdate(data.id, {
        planner: {
          ...current,
          sellingPoints: [],
          sellingPointNodeIds: [],
        },
      });
      return;
    }

    // 手动创建：若已存在节点则不重复创建
    if (existingIds.length > 0) return;

    if (!onAddNode) return;

    // 自己编写内容节点尺寸（2x 放大后）
    const TEXT_NODE_WIDTH = 840;
    const TEXT_NODE_HEIGHT = 472;
    const PLANNER_WIDTH = 728;
    const PLANNER_HEIGHT = 1040;
    const GAP_Y = 200; // 策划师节点底部间距

    // 位置：策划师节点底部，水平居中
    const textX = data.x + (PLANNER_WIDTH - TEXT_NODE_WIDTH) / 2;
    const textY = data.y + PLANNER_HEIGHT + GAP_Y;

    const nextIndex = useNodeStore.getState().getNextTextNodeIndex();
    const nodeId = onAddNode(
      NodeType.PROMPT_INPUT,
      { x: textX, y: textY },
      {
        subMode: 'self-write',
        editMode: 'preview',
        textNodeIndex: nextIndex,
        nodeSize: { width: TEXT_NODE_WIDTH, height: TEXT_NODE_HEIGHT },
      }
    );

    if (nodeId) {
      // 连接到策划师节点的左边连接点（target handle）
      useNodeStore.getState().addConnection(nodeId, data.id);
      // 命名为"产品卖点"
      useNodeStore.getState().updateNode(nodeId, {}, undefined, '产品卖点');
    }

    onNodeUpdate(data.id, {
      planner: {
        ...current,
        sellingPoints: [{ id: '1', text: '', source: 'manual' }],
        sellingPointNodeIds: nodeId ? [nodeId] : [],
      },
    });
  }, [data.id, data.x, data.y, onAddNode, onNodeUpdate]);

  /** planner 自动应答规则（start 与 §5.8 恢复共用；恢复时点重新收集——输入均已持久化在节点数据） */
  const buildPlannerAutoReplyRules = useCallback((): NodeSkillAutoReplyRule[] => {
    const current = readCurrentPlanner(data.id);
    // 面板管理数组 + 手动连线接入合并收集
    const mergedCompetitorIds = [
      ...(current.competitorImageNodeIds ?? []),
      ...(current.connectedCompetitorImageNodeIds ?? []),
    ];
    // 合并后数量为 0 = 跳过竞品分析（与启动卡 stepper 语义一致）
    const skipCompetitor = current.skipCompetitor || mergedCompetitorIds.length <= 0;
    const competitorIds = skipCompetitor ? [] : mergedCompetitorIds;
    return [
      {
        workflowStep: 'product_receiving',
        reply: (ask) => {
          if (skipCompetitor) return '跳过';
          const competitorAssets = collectAssetAttachments(competitorIds);
          if (competitorAssets.length > 0 && competitorAssets.length === competitorIds.length) {
            return ask.uploadRoleText || '我已上传竞品图，请继续';
          }
          return undefined;
        },
        attachments: () => {
          if (skipCompetitor) return [];
          return collectAssetAttachments(competitorIds);
        },
      },
      // 智能推荐（style 为空）：回复「选第一个」采纳主推风格（prompt.md 阶段二的明确表述，直接推进到文案策划）
      { workflowStep: 'style_selection', reply: () => current.style || '选第一个' },
    ];
  }, [data.id]);

  const runPlannerGeneration = useCallback(async () => {
    const error = validatePlanner();
    if (error) {
      toast.warning(error);
      return;
    }

    const current = readCurrentPlanner(data.id);
    // 面板管理数组 + 手动连线接入合并收集
    const mergedProductIds = [
      ...(current.productImageNodeIds ?? []),
      ...(current.connectedProductImageNodeIds ?? []),
    ];
    const productAssets = collectAssetAttachments(mergedProductIds);
    if (productAssets.length === 0) {
      toast.warning('产品图节点尚未上传图片，请先上传产品图');
      return;
    }

    const store = useNodeStore.getState();
    const sellingPointText = (current.sellingPointNodeIds ?? [])
      .map((id) => ((store.nodeMap.get(id)?.data as { textContent?: string } | undefined)?.textContent ?? '').trim())
      .filter(Boolean)
      .join('\n');

    const messageLines = [
      '请为我的产品策划电商详情页。',
      `- 产品图已通过附件上传（${productAssets.length} 张）`,
    ];
    if (sellingPointText) {
      messageLines.push(`- 产品卖点：${sellingPointText}`);
    }

    // P2.5 长图切屏：启动前扫描竞品图，第一张多屏长图的边界候选行清单随启动消息附带
    // （契约：competitive_analysis 收到候选行清单时输出【切屏边界确认表】；失败/无长图回落整图模式）
    const mergedCompetitorIds = [
      ...(current.competitorImageNodeIds ?? []),
      ...(current.connectedCompetitorImageNodeIds ?? []),
    ];
    if (!current.skipCompetitor && mergedCompetitorIds.length > 0) {
      try {
        const competitorAssets = collectAssetAttachments(mergedCompetitorIds);
        if (competitorAssets.length === 0) console.info('[P2.5] 竞品图资产为空，跳过长图扫描（将按整图分析竞品）');
        for (const asset of competitorAssets) {
          const scan = await scanCompetitorImage(asset.dataUrl);
          if (!scan.isLong) {
            console.info('[P2.5] 竞品图非长图，跳过切屏:', asset.name, `${scan.width}x${scan.height}`);
            continue;
          }
          if (scan.candidates.length === 0) {
            console.info('[P2.5] 长图未检出边界候选（屏间留白不足），回落整图模式:', asset.name, `${scan.width}x${scan.height}`);
            continue;
          }
          // 预计屏数：下限估计——✓推荐候选行（空带色带/装饰分隔带）是模块断点的像素级证据，
          // 模块数 ≥ ✓行数+1 恒成立；满铺设计图上通道一零命中时 ✓推荐稀疏，此值会严重低估真实模块数，
          // 故只作下限锚点输出（禁止当等式用：实测「±2 硬闸」把 LLM 框死在 3±2 屏致 7 模块并一屏）
          const okCount = scan.candidates.filter((c) => c.tag === 'ok').length;
          const expectedScreens = Math.min(15, Math.max(3, okCount + 1));
          messageLines.push(
            [
              `【边界候选行清单】（附件「${asset.name}」为多屏拼接长图，切屏参考用）`,
              ...scan.candidates.map((c) => {
                const mark = c.tag === 'ok' ? '✓推荐' : c.tag === 'bad' ? '⚠慎选' : '·';
                return `- 候选行位置 ${c.posPct.toFixed(1)}% ${mark}：${c.note ?? '密度分界（未标注形态）'}（置信度 ${c.confidence.toFixed(2)}）`;
              }),
              `【预计屏数】该长图 ${scan.width}x${scan.height}，按✓推荐候选行估计至少含 ${expectedScreens} 个内容模块（✓推荐行是模块断点的像素级证据，画面可见模块多于该数时以画面为准，切分屏数不得少于该估计）；模块高度天然不均，禁止为边界间距均衡而并屏；质检报告/证书等佐证内容归属其对应的编号卖点模块（NO.x 的佐证与 NO.x 同屏），不独立计屏`,
            ].join('\n'),
          );
          // 候选清单同步落库（仅存于启动消息会随会话丢失）：解析确认表时按此吸附/丢弃校验
          useNodeStore.getState().updateNode(data.id, {
            planner: { ...readCurrentPlanner(data.id), screenBoundaryCandidates: scan.candidates.map((c) => c.posPct) },
          });
          break;
        }
      } catch (err) {
        console.error('[P2.5] 竞品长图扫描失败，回落整图模式:', err);
        toast.warning('竞品长图切屏扫描失败，将按整图分析竞品');
      }
    }

    // §5.8 回复通道失效恢复：error 态重跑复用原 sessionId（后端凭持久化 wf_state 恢复到挂起步骤）
    const persistedRunState = current.runState;
    const resumeSessionId = persistedRunState?.status === 'error' ? persistedRunState.sessionId : undefined;

    // 进入流水线 PLANNING 阶段（CONFIG 启动与 error 重跑共用此入口）
    const currentData = useNodeStore.getState().nodeMap.get(data.id)?.data as DetailPageData | undefined;
    if (currentData && deriveStage(currentData) !== DetailPageStage.PLANNING) {
      useNodeStore.getState().updateNode(data.id, { stage: DetailPageStage.PLANNING });
    }

    plannerSession.start(
      'page_design',
      messageLines.join('\n'),
      {
        attachments: productAssets,
        // model 为空 = 跟随全局设置（智能体当前模型），可在节点高级配置中覆盖
        modelId: current.model || useAgentStore.getState().currentModelId,
        autoReplyRules: buildPlannerAutoReplyRules(),
        stopAfterSkill: true,
        ...(resumeSessionId ? { sessionId: resumeSessionId } : {}),
        onError: (err) => toast.error(`策划执行失败：${err}`),
      },
    );
  }, [data.id, validatePlanner, plannerSession, buildPlannerAutoReplyRules]);

  // §5.8 挂载恢复（planner）：画布加载发现持久化 runState ∈ {running, waiting_ask} 时接管；
  // StrictMode 双跑由 resume 内部 activeRef 守卫；完结解析由上方既有 runState effect 触发
  const plannerResumeRef = useRef(false);
  useEffect(() => {
    if (plannerResumeRef.current) return;
    const rs = readCurrentPlanner(data.id).runState;
    if (!rs || (rs.status !== 'running' && rs.status !== 'waiting_ask')) return;
    plannerResumeRef.current = true;
    plannerSession.resume(rs, { autoReplyRules: buildPlannerAutoReplyRules() });
  }, [data.id, plannerSession, buildPlannerAutoReplyRules]);

  /** designer 执行（§5.4）：page_prompt skill 继承 planner 的 sessionId（同 session 才命中 _load_inherit_context 路径A） */
  const runDesignerGeneration = useCallback(() => {
    const current = readCurrentDesigner(data.id);
    // 同节点流水线：优先 designer 持久化 runState，兜底同节点 planner 的实时 sessionId（链式初始化前）
    const inheritedSessionId = current.runState?.sessionId ?? readCurrentPlanner(data.id).runState?.sessionId;
    if (!inheritedSessionId) {
      toast.warning('缺少策划会话上下文，请先完成详情页策划');
      return;
    }
    const planner = readCurrentPlanner(data.id);
    // P1 文字通道：启动消息附带竞品风格档案与规避项（切片缺失时静默回落风格预设链路，与现状一致）
    const messageLines = ['请基于已确认的详情页策划方案，生成分屏提示词。'];
    if (planner.competitorStyleProfile) {
      messageLines.push('【竞品风格档案】', planner.competitorStyleProfile);
    }
    if (planner.competitorStyleBlacklist) {
      messageLines.push('【须规避项】', planner.competitorStyleBlacklist);
    }
    designerSession.start(
      'page_prompt',
      messageLines.join('\n'),
      {
        sessionId: inheritedSessionId,
        modelId: current.model || planner.model || useAgentStore.getState().currentModelId,
        stopAfterSkill: true,
        onError: (err) => toast.error(`分屏提示词生成失败：${err}`),
      },
    );
  }, [data.id, designerSession]);

  // §5.8 挂载恢复（designer）：同构 planner；page_prompt 无交互步骤，无需自动应答规则
  const designerResumeRef = useRef(false);
  useEffect(() => {
    if (designerResumeRef.current) return;
    const rs = readCurrentDesigner(data.id).runState;
    if (!rs || (rs.status !== 'running' && rs.status !== 'waiting_ask')) return;
    designerResumeRef.current = true;
    designerSession.resume(rs);
  }, [data.id, designerSession]);

  const runDesignerRef = useRef(runDesignerGeneration);
  runDesignerRef.current = runDesignerGeneration;

  /**
   * 链式推进执行器：初始化同节点 designer 数据（sessionId 继承）并启动 page_prompt。
   * 非破坏式：保留既有 designer 数据（promptResult/splitPrompts 等），重复调用安全。
   */
  const chainToDesigning = useCallback(() => {
    const store = useNodeStore.getState();
    const nodeData = store.nodeMap.get(data.id)?.data as DetailPageData | undefined;
    const upPlanner = nodeData?.planner;
    if (!upPlanner?.isPlannerComplete) return;

    const prevDesigner = nodeData?.designer;
    const sectionCount = countArchitectureBlocks(upPlanner.architectureTable);
    const hasSd = /S\/D|首屏|购买/i.test(upPlanner.architectureTable || '');
    const architectureSummary = sectionCount > 0
      ? `共${sectionCount}区块${hasSd ? '，含S/D版式' : ''}`
      : '已生成架构表';

    const inheritedSessionId = prevDesigner?.runState?.sessionId ?? upPlanner.runState?.sessionId;

    // 同步写 store：runDesignerGeneration 内部立即读取 designer 数据（sessionId/model）
    store.updateNode(data.id, {
      stage: DetailPageStage.DESIGNING,
      designer: {
        ...defaultDesignerData(),
        ...prevDesigner,
        architectureSummary,
        style: upPlanner.style,
        model: prevDesigner?.model || upPlanner.model,
        hasUpstreamData: true,
        ...(inheritedSessionId
          ? { runState: { status: 'idle' as const, sessionId: inheritedSessionId } }
          : {}),
      },
    });
    runDesignerRef.current();
  }, [data.id]);

  /** E3 重跑分屏提示词入口：同一次 updateNode 清空出图记录与旧切块并回写 stage=DESIGNING（获得执行态布局/贴底执行条/执行中徽标），完成后由 applyDesignerResult 切回 CONSUME */
  const regenerateDesignerPrompts = useCallback(() => {
    const store = useNodeStore.getState();
    const nodeData = store.nodeMap.get(data.id)?.data as DetailPageData | undefined;
    if (!nodeData) return;
    store.updateNode(data.id, {
      stage: DetailPageStage.DESIGNING,
      generatedBlocks: [],
      designer: { ...readCurrentDesigner(data.id), splitPrompts: [], generatedBlockNodeIds: {} },
    });
    runDesignerRef.current();
  }, [data.id]);

  /** 存量补救（解析契约修复后回填）：promptResult 已有全文但 splitPrompts 为空或含旧兜底标题（「第N区块」，主文案提取契约前的产物）时重新解析写入（不动 stage/runState；applyDesignerResult 对相同全文幂等短路，故此处独立解析写入）；解析无变化时跳过写入。执行中会话跳过防竞争，由流完结自行解析 */
  const reparseDesignerPrompts = useCallback(() => {
    const nodeData = useNodeStore.getState().nodeMap.get(data.id)?.data as DetailPageData | undefined;
    if (!nodeData) return;
    const designer = readCurrentDesigner(data.id);
    if (!designer?.promptResult) return;
    const existing = designer.splitPrompts ?? [];
    if (existing.length > 0 && !existing.some((block) => LEGACY_FALLBACK_TITLE_RE.test(block.title))) return;
    if (designerSession.runState.status === 'running') return;
    const splitPrompts = parseSplitPrompts(designer.promptResult);
    if (splitPrompts.length === 0) return;
    if (JSON.stringify(splitPrompts) === JSON.stringify(existing)) return;
    useNodeStore.getState().updateNode(data.id, {
      designer: { ...readCurrentDesigner(data.id), splitPrompts },
    });
  }, [data.id, designerSession.runState.status]);

  /**
   * PLANNING→DESIGNING 自动链式（单节点流水线核心，替代旧跨节点桥）：
   * 触发条件 = 节点 stage 推导为 PLANNING 且策划已完成。
   * - 会话内：planner 流 done（completed）→ effect 捕获；
   * - 挂载续跑：持久化 runState 已完结（旧 mode=PLANNER 数据兜底）→ 挂载首次执行即捕获。
   * 幂等：链式写入 stage=DESIGNING 后条件不再成立；会话执行中/挂起/出错均不触发。
   */
  const chainLockRef = useRef(false);
  useEffect(() => {
    if (chainLockRef.current) return;
    const nodeData = useNodeStore.getState().nodeMap.get(data.id)?.data as DetailPageData | undefined;
    if (!nodeData) return;
    const stage = deriveStage(nodeData);
    const persisted = nodeData.planner?.runState;
    const sessionCompleted = plannerSession.runState.status === 'completed';
    const persistedCompleted = !!persisted && persisted.status === 'completed';

    if (stage === DetailPageStage.PLANNING) {
      // 常规链式：策划流完结（会话内或持久化完结）→ 推进 DESIGNING
      if (!sessionCompleted && !persistedCompleted) return;
    } else if (stage === DetailPageStage.DESIGNING) {
      // 窄窗口补救：链式已切 DESIGNING 但 designer 会话未启动（刷新/异常落在初始化写入与执行之间），
      // 恢复后链式（只认 PLANNING）、resume（只认 running/waiting_ask）、执行条（idle 不渲染）三者均不触发 → 死态。
      // 仅限 designer 从未产出（无 promptResult 且无分块）且运行态为 idle 时补启动；
      // 完成终态必切 CONSUME（applyDesignerResult），故该形态不会覆盖既有结果
      const designer = nodeData.designer;
      if (designer?.promptResult || (designer?.splitPrompts?.length ?? 0) > 0) return;
      if (designer?.runState && designer.runState.status !== 'idle') return;
      if (designerSession.runState.status !== 'idle') return;
      if (!nodeData.planner?.isPlannerComplete) return;
    } else {
      return;
    }
    chainLockRef.current = true;
    chainToDesigning();
  }, [data.id, plannerSession.runState.status, designerSession.runState.status, chainToDesigning]);

  /** 读取当前已生成分块的索引集合（store 实时） */
  function readGeneratedBlocks(nodeId: string): number[] {
    const nodeData = useNodeStore.getState().nodeMap.get(nodeId)?.data as DetailPageData | undefined;
    return nodeData?.generatedBlocks ?? [];
  }

  /**
   * 单块出图：按分块提示词创建图片生成节点（已生成过的分块去重跳过），
   * 画幅比例按版式单元映射（S=9:16 / D=9:21 / T=1:3），创建后经统一编排入口自动触发生图
   */
  const generateBlockImage = useCallback((block: SplitPrompt): string | undefined => {
    if (!onAddNode) {
      toast.warning('当前环境不支持创建节点');
      return undefined;
    }
    const generated = readGeneratedBlocks(data.id);
    if (generated.includes(block.index)) {
      toast.info(`第${block.index}区块已生成过图片节点`);
      return undefined;
    }
    // 竖排堆叠：按分块序号顺序填充，每竖排最多 4 个、满 4 个横向开新竖排，槽位按分块序号固定（任意顺序生成互不碰撞）；
    // 各竖排内部行距统一为小间隙（排内 y = 排顶 + 前序同排节点占位高度累加 + 前序同排节点数 × 统一间隙，
    // 卡片高矮不一排内也严格等距）；
    // 排高按全部分块预算取最高排，整组垂直居中于详情页节点（实测高度优先，measured 未就绪回退常量）
    const allBlocks = readCurrentDesigner(data.id).splitPrompts ?? [];
    const ordered = (allBlocks.length > 0 ? [...allBlocks] : [block]).sort((a, b) => a.index - b.index);
    const colOf = (index: number) => Math.floor((index - 1) / BLOCK_STACK_PER_COLUMN);
    const colCount = Math.max(...ordered.map((b) => colOf(b.index))) + 1;
    const colSpan = Array.from({ length: colCount }, (_, c) => {
      const colIdx = ordered.filter((b) => colOf(b.index) === c);
      return colIdx.reduce((sum, b) => sum + blockNodeFootprintHeight(b.unitType), 0)
        + Math.max(colIdx.length - 1, 0) * BLOCK_STACK_GAP_Y;
    });
    const detailHeight = reactFlowInstance.getInternalNode(data.id)?.measured?.height ?? DETAIL_PAGE_HEIGHT_FALLBACK;
    const groupTop = data.y + detailHeight / 2 - Math.max(...colSpan) / 2;
    const col = colOf(block.index);
    const preceding = ordered.filter((b) => colOf(b.index) === col && b.index < block.index);
    const position = {
      x: data.x + 1000 + col * (BLOCK_NODE_SHORT_EDGE + BLOCK_STACK_GAP_X),
      y: groupTop
        + preceding.reduce((sum, b) => sum + blockNodeFootprintHeight(b.unitType), 0)
        + preceding.length * BLOCK_STACK_GAP_Y,
    };
    // 高级设置选择的生图模型透传到图片节点（空 = 跟随默认 grsai-gpt-image-2）
    const imageModel = readCurrentPlanner(data.id).imageModel;
    const newId = onAddNode(NodeType.IMAGE_GENERATOR, position, {
      prompt: block.content,
      generationMode: 'TXT2IMG',
      aspectRatio: UNIT_TYPE_ASPECT_RATIO[block.unitType] ?? '3:4',
      unitType: block.unitType,
      ...(imageModel ? { model: imageModel } : {}),
    });
    if (!newId) return undefined;
    const store = useNodeStore.getState();
    store.addConnection(data.id, newId);
    // addConnection 的连线副作用会把 TXT2IMG 翻转为 IMG2IMG：详情页出图连线会把产品图传给分块节点作参考图
    // （orchestrator 展开 planner 产品图为图生图输入，与画布手动连线行为一致），不再复位文生图
    store.updateNode(newId, {}, undefined, [formatBlockLabel(block.index), resolveBlockDisplayTitle(block)].filter(Boolean).join('·'));
    // 自动副作用：登记分块索引与出图节点 id 映射，直接写 store，不记撤销历史（映射供删除出图节点时回退登记）
    const designer = readCurrentDesigner(data.id);
    store.updateNode(data.id, {
      generatedBlocks: [...generated, block.index],
      designer: {
        ...designer,
        generatedBlockNodeIds: { ...(designer.generatedBlockNodeIds ?? {}), [block.index]: newId },
      },
    });
    // 创建即触发生图：handleNodeAction 实时读 store（新建节点同步可见），复用画布统一的
    // 生成链路（状态流转/图生图参考图收集/任务注册/刷新恢复），批量路径逐块调用等效并行生成
    onNodeAction?.(newId);
    return newId;
  }, [data.id, data.x, data.y, onAddNode, onNodeAction, reactFlowInstance]);

  /**
   * 「确认生成」（一键全部，节点 CONSUME 区与提示词结果节点尝试项共用）：
   * 读取 designer.splitPrompts，对未生成过的分块逐个创建图片生成节点并连线
   */
  const confirmGenerateSplitPromptNodes = useCallback(() => {
    const prompts = readCurrentDesigner(data.id).splitPrompts ?? [];
    if (prompts.length === 0) {
      toast.warning('暂无分屏提示词，请先完成分屏提示词生成');
      return;
    }
    const generated = readGeneratedBlocks(data.id);
    const pending = prompts.filter((block) => !generated.includes(block.index));
    if (pending.length === 0) {
      toast.info('全部分屏图片节点均已生成');
      return;
    }
    let created = 0;
    pending.forEach((block) => {
      if (generateBlockImage(block)) created += 1;
    });
    if (created > 0) toast.success(`已按分屏提示词创建 ${created} 个节点并加入生成队列`);
  }, [data.id, generateBlockImage]);

  return {
    plannerSession,
    designerSession,
    syncProductImageNodes,
    syncCompetitorImageNodes,
    skipCompetitorAnalysis,
    syncSellingPointNode,
    runPlannerGeneration,
    runDesignerGeneration,
    regenerateDesignerPrompts,
    reparseDesignerPrompts,
    generateBlockImage,
    confirmGenerateSplitPromptNodes,
  };
}
